import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FeatherIcon } from '../../components/FeatherIcon'
import type { Page } from '../../crm/client/pager'
import { ListLoading } from '../../library/ListLoading'
import { Sentinel } from '../../library/Sentinel'
import { usePagedList } from '../../library/usePagedList'
import { useMediaSrc } from '../../library/useMediaSrc'
import { Avatar } from '../Avatar'
import type { OfApi } from '../ofApi'
import type { OfUser } from '../posts'
import { ConfirmPopover } from '../../library/ConfirmPopover'
import { Dialog } from './Dialog'
import { defaultScheduleTime, EXPIRY_DAYS, PRICE_MIN, TARGET_MIN, toLabel, toTaggable, type Label } from './homeData'

/** The composer's pickers (SPEC §1 composer panels, screenshots home_comp_*.png). */

const REFERER = 'https://onlyfans.com/posts/create'
type ListPayload = { list?: Record<string, unknown>[]; hasMore?: boolean; nextOffset?: number }

// ── Add labels ──────────────────────────────────────────────────────────────

const labelId = (l: Label) => l.id

export function LabelsPicker({ of, chosen, onChange, onClose }: { of: OfApi; chosen: Label[]; onChange: (labels: Label[]) => void; onClose: () => void }) {
  const { t } = useTranslation('ofHome')
  const [creating, setCreating] = useState(false)
  const [newName, setNewName] = useState('')
  const [createErr, setCreateErr] = useState('')
  const [busy, setBusy] = useState(false)
  const [renaming, setRenaming] = useState<Label | null>(null)
  const [renameTo, setRenameTo] = useState('')
  const [deleting, setDeleting] = useState<Label | null>(null)
  const fetchLabels = useCallback(
    async (offset: number): Promise<Page<Label>> => {
      const p = await of.get<ListPayload>('labels', { offset, limit: 10 }, REFERER)
      return { list: (p?.list ?? []).map(toLabel), hasMore: p?.hasMore, nextOffset: p?.nextOffset }
    },
    [of],
  )
  const labels = usePagedList(fetchLabels, labelId)
  const on = new Set(chosen.map((l) => l.id))
  const toggle = (l: Label) => onChange(on.has(l.id) ? chosen.filter((x) => x.id !== l.id) : [...chosen, l])

  const create = async () => {
    const name = newName.trim()
    if (!name || busy) return
    setBusy(true)
    setCreateErr('')
    try {
      // Live OF create body is `{ name }` only (type is assigned server-side).
      const raw = await of.send<Record<string, unknown>>('POST', 'labels', REFERER, { name })
      const created = toLabel((raw ?? { id: Date.now(), name, postsCount: 0, posts: [] }) as Record<string, unknown>)
      onChange([...chosen.filter((x) => x.id !== created.id), created])
      setNewName('')
      setCreating(false)
      await labels.reload()
    } catch (e) {
      setCreateErr(e instanceof Error && e.message ? e.message : t('error'))
    } finally {
      setBusy(false)
    }
  }

  const rename = async () => {
    if (!renaming || busy) return
    const name = renameTo.trim()
    if (!name || name === renaming.name) {
      setRenaming(null)
      return
    }
    setBusy(true)
    setCreateErr('')
    try {
      try {
        await of.send('PATCH', `labels/${renaming.id}`, REFERER, { name })
      } catch {
        await of.send('PUT', `labels/${renaming.id}`, REFERER, { name })
      }
      labels.setItems((list) => list.map((x) => (x.id === renaming.id ? { ...x, name } : x)))
      onChange(chosen.map((x) => (x.id === renaming.id ? { ...x, name } : x)))
      setRenaming(null)
    } catch (e) {
      setCreateErr(e instanceof Error && e.message ? e.message : t('error'))
    } finally {
      setBusy(false)
    }
  }

  const remove = async () => {
    if (!deleting || busy) return
    setBusy(true)
    setCreateErr('')
    try {
      await of.send('DELETE', `labels/${deleting.id}`, REFERER, null)
      labels.setItems((list) => list.filter((x) => x.id !== deleting.id))
      onChange(chosen.filter((x) => x.id !== deleting.id))
      setDeleting(null)
    } catch (e) {
      setCreateErr(e instanceof Error && e.message ? e.message : t('error'))
    } finally {
      setBusy(false)
    }
  }

  const move = async (l: Label, dir: -1 | 1) => {
    const idx = labels.items.findIndex((x) => x.id === l.id)
    const j = idx + dir
    if (idx < 0 || j < 0 || j >= labels.items.length || busy) return
    const next = [...labels.items]
    ;[next[idx], next[j]] = [next[j]!, next[idx]!]
    labels.setItems(next)
    setBusy(true)
    setCreateErr('')
    const order = next.map((x) => x.id).filter((id) => Number.isFinite(id))
    try {
      try {
        await of.send('PUT', 'labels/order', REFERER, { labelIds: order })
      } catch {
        await of.send('POST', 'labels/sort', REFERER, { labelIds: order })
      }
    } catch (e) {
      setCreateErr(e instanceof Error && e.message ? e.message : t('error'))
      await labels.reload()
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog
      title={t('labels.title')}
      onClose={onClose}
      actions={
        <button type="button" className="lib-btn lib-btn--primary" onClick={onClose}>
          {t('close')}
        </button>
      }
    >
      <div className="ofh-labels-create">
        {creating ? (
          <>
            <input
              className="ofp-input"
              value={newName}
              maxLength={64}
              placeholder={t('labels.namePlaceholder')}
              aria-label={t('labels.namePlaceholder')}
              autoFocus
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && void create()}
            />
            <button type="button" className="lib-btn lib-btn--primary" disabled={busy || !newName.trim()} onClick={() => void create()}>
              {t('labels.create')}
            </button>
            <button type="button" className="lib-btn lib-btn--ghost" onClick={() => (setCreating(false), setCreateErr(''))}>
              {t('cancel')}
            </button>
          </>
        ) : (
          <button type="button" className="lib-btn" onClick={() => setCreating(true)}>
            <FeatherIcon name="plus" size={14} />
            {t('labels.create')}
          </button>
        )}
      </div>
      {createErr && (
        <p className="ofp-error" role="alert">
          {createErr}
        </p>
      )}
      <div className="ofh-pick-list" role="group" aria-label={t('labels.title')}>
        {labels.items.map((l, i) => (
          <div key={l.id} className="ofh-pick ofh-pick--row">
            {renaming?.id === l.id ? (
              <div className="ofh-pick-main ofh-pick-main--edit">
                <input
                  className="ofp-input"
                  value={renameTo}
                  maxLength={64}
                  autoFocus
                  aria-label={t('labels.rename')}
                  onChange={(e) => setRenameTo(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && void rename()}
                />
                <button type="button" className="lib-btn lib-btn--primary" disabled={busy || !renameTo.trim()} onClick={() => void rename()}>
                  {t('labels.rename')}
                </button>
                <button type="button" className="lib-btn lib-btn--ghost" onClick={() => setRenaming(null)}>
                  {t('cancel')}
                </button>
              </div>
            ) : (
              <label className="ofh-pick-main">
                <input type="checkbox" className="ofh-pick-box" checked={on.has(l.id)} onChange={() => toggle(l)} />
                <span className="ofh-pick-text">
                  <span className="ofh-pick-name">{l.name}</span>
                  <span className="ofh-pick-sub">{l.posts ? t('labels.posts', { count: l.posts }) : t('labels.empty')}</span>
                </span>
                <span className="ofh-pick-thumbs">
                  {l.previews.slice(0, 3).map((url) => (
                    <SmallThumb key={url} url={url} />
                  ))}
                </span>
              </label>
            )}
            <span className="ofh-pick-order">
              <button
                type="button"
                className="lib-icon-btn"
                aria-label={t('labels.rename')}
                disabled={busy}
                onClick={() => (setRenaming(l), setRenameTo(l.name), setDeleting(null))}
              >
                <FeatherIcon name="edit-2" size={14} />
              </button>
              <button type="button" className="lib-icon-btn" aria-label={t('labels.delete')} disabled={busy} onClick={() => setDeleting(l)}>
                <FeatherIcon name="trash-2" size={14} />
              </button>
              <button type="button" className="lib-icon-btn" aria-label={t('labels.moveUp')} disabled={busy || i === 0} onClick={() => void move(l, -1)}>
                <FeatherIcon name="chevron-up" size={14} />
              </button>
              <button
                type="button"
                className="lib-icon-btn"
                aria-label={t('labels.moveDown')}
                disabled={busy || i === labels.items.length - 1}
                onClick={() => void move(l, 1)}
              >
                <FeatherIcon name="chevron-down" size={14} />
              </button>
            </span>
          </div>
        ))}
        {labels.hasMore && !labels.failed && <Sentinel onVisible={labels.loadMore} token={labels.items.length} />}
      </div>
      {deleting && (
        <ConfirmPopover
          title={t('labels.deleteTitle', { name: deleting.name })}
          body={t('labels.deleteBody')}
          confirmLabel={t('labels.delete')}
          onConfirm={() => void remove()}
          onCancel={() => setDeleting(null)}
        />
      )}
      <ListLoading loading={labels.loading} empty={!labels.items.length} />
      {!labels.loading && !labels.hasMore && labels.items.length === 0 && <p className="lib-empty">{t('labels.none')}</p>}
      {labels.failed && <p className="ofp-error">{t('error')}</p>}
    </Dialog>
  )
}

function SmallThumb({ url }: { url: string }) {
  const { ref, src } = useMediaSrc(url)
  return <span ref={ref as React.RefObject<HTMLSpanElement>} className="ofh-thumb">{src && <img src={src} alt="" />}</span>
}

// ── Tag other creators ──────────────────────────────────────────────────────

const userId = (u: OfUser) => u.id

export function TagPicker({ of, chosen, onChange, onClose }: { of: OfApi; chosen: OfUser[]; onChange: (users: OfUser[]) => void; onClose: () => void }) {
  const { t } = useTranslation('ofHome')
  const fetchUsers = useCallback(
    async (offset: number): Promise<Page<OfUser>> => {
      const p = await of.get<{ items?: unknown[]; hasMore?: boolean }>(
        'posts/tagged-friend-users',
        { limit: 50, offset, skip_users: 'all', filter: 'all', sort: 'date:desc' },
        REFERER,
      )
      const users = toTaggable(p)
      return { list: users, hasMore: p?.hasMore === true, nextOffset: offset + users.length }
    },
    [of],
  )
  const users = usePagedList(fetchUsers, userId)
  const on = new Set(chosen.map((u) => u.id))
  const toggle = (u: OfUser) => onChange(on.has(u.id) ? chosen.filter((x) => x.id !== u.id) : [...chosen, u])
  return (
    <Dialog
      title={t('tag.title')}
      onClose={onClose}
      actions={
        <button type="button" className="lib-btn lib-btn--primary" onClick={onClose}>
          {t('close')}
        </button>
      }
    >
      <div className="ofh-pick-list" role="group" aria-label={t('tag.title')}>
        {users.items.map((u) => (
          <label key={u.id} className="ofh-pick">
            <input type="checkbox" className="ofh-pick-box" checked={on.has(u.id)} onChange={() => toggle(u)} />
            <Avatar user={u} size={40} />
            <span className="ofh-pick-text">
              <span className="ofh-pick-name">{u.name}</span>
              <span className="ofh-pick-sub">
                {u.verified ? `${t('tag.verified')} · ` : ''}@{u.username}
              </span>
            </span>
          </label>
        ))}
        {users.hasMore && !users.failed && <Sentinel onVisible={users.loadMore} token={users.items.length} />}
      </div>
      <ListLoading loading={users.loading} empty={!users.items.length} />
      {!users.loading && !users.hasMore && users.items.length === 0 && <p className="lib-empty">{t('tag.none')}</p>}
      {users.failed && <p className="ofp-error">{t('error')}</p>}
    </Dialog>
  )
}

// ── Amount (post price / fundraising target) ────────────────────────────────

export function AmountDialog({ kind, title, value, onSave, onClose }: { kind: 'price' | 'target'; title?: string; value: number | null; onSave: (v: number | null) => void; onClose: () => void }) {
  const { t } = useTranslation('ofHome')
  const [text, setText] = useState(value ? String(value) : '')
  const n = text.trim() === '' ? null : Number(text.replace(',', '.'))
  const min = kind === 'price' ? PRICE_MIN : TARGET_MIN
  const valid = n === null ? kind === 'price' || value !== null : Number.isFinite(n) && (n >= min || (kind === 'price' && n === 0))
  const save = () => onSave(n === null || n === 0 ? null : Math.round(n * 100) / 100)
  return (
    <Dialog
      title={title ?? t(`${kind}.title`)}
      onClose={onClose}
      actions={
        <>
          <button type="button" className="lib-btn lib-btn--ghost" onClick={onClose}>
            {t('cancel')}
          </button>
          <button type="button" className="lib-btn lib-btn--primary" disabled={!valid} onClick={save}>
            {t('save')}
          </button>
        </>
      }
    >
      <label className="ofh-amount">
        <span className="ofh-amount-cur">$</span>
        <input
          className="ofp-input"
          inputMode="decimal"
          aria-label={t(`${kind}.label`)}
          placeholder={t('free')}
          value={text}
          autoFocus
          onChange={(e) => setText(e.target.value.replace(/[^\d.,]/g, ''))}
          onKeyDown={(e) => e.key === 'Enter' && valid && save()}
        />
      </label>
      <p className={`ofp-modal-note ${n !== null && !valid ? 'ofh-note-bad' : ''}`}>{t(`${kind}.note`, { min })}</p>
    </Dialog>
  )
}

// ── Expiration period ───────────────────────────────────────────────────────

export function ExpiryDialog({ value, onSave, onClose }: { value: number | null; onSave: (days: number | null) => void; onClose: () => void }) {
  const { t } = useTranslation('ofHome')
  const [days, setDays] = useState<number | null>(value)
  const [text, setText] = useState(value ? String(value) : '')
  const valid = days === null || (Number.isInteger(days) && days > 0)
  const pick = (d: number | null) => {
    setDays(d)
    setText(d ? String(d) : '')
  }
  return (
    <Dialog
      title={t('expiry.title')}
      onClose={onClose}
      actions={
        <>
          <button type="button" className="lib-btn lib-btn--ghost" onClick={onClose}>
            {t('cancel')}
          </button>
          <button type="button" className="lib-btn lib-btn--primary" disabled={!valid || days === value} onClick={() => onSave(days)}>
            {t('save')}
          </button>
        </>
      }
    >
      <input
        className="ofp-input"
        inputMode="numeric"
        aria-label={t('expiry.days')}
        placeholder={t('expiry.noLimit')}
        value={text}
        onChange={(e) => {
          const v = e.target.value.replace(/\D/g, '')
          setText(v)
          setDays(v ? Number(v) : null)
        }}
      />
      <p className="ofp-modal-note">{t('expiry.note')}</p>
      <div className="lib-chips" role="radiogroup" aria-label={t('expiry.title')}>
        {[null, ...EXPIRY_DAYS].map((d) => (
          <button key={String(d)} type="button" role="radio" aria-checked={days === d} className={`lib-chip ${days === d ? 'is-on' : ''}`} onClick={() => pick(d)}>
            {d === null ? t('expiry.noLimit') : t('expiry.daysN', { count: d })}
          </button>
        ))}
      </div>
    </Dialog>
  )
}

// ── Schedule post ───────────────────────────────────────────────────────────

const pad = (n: number) => String(n).padStart(2, '0')
const dateValue = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
const timeValue = (d: Date) => `${pad(d.getHours())}:${pad(d.getMinutes())}`

export function ScheduleDialog({
  title,
  laterNote,
  value,
  onSchedule,
  onLater,
  onClose,
}: {
  /** Defaults to the post wording (the New message passes its own). */
  title?: string
  laterNote?: string
  value: Date | null
  onSchedule: (at: Date) => void
  onLater: () => void
  onClose: () => void
}) {
  const { t } = useTranslation('ofHome')
  const start = value ?? defaultScheduleTime()
  const [date, setDate] = useState(dateValue(start))
  const [time, setTime] = useState(timeValue(start))
  const at = new Date(`${date}T${time || '00:00'}`)
  const valid = !Number.isNaN(at.getTime()) && at.getTime() > Date.now()
  return (
    <Dialog
      title={title ?? t('schedule.title')}
      onClose={onClose}
      actions={
        <>
          <button type="button" className="lib-btn lib-btn--ghost" onClick={onClose}>
            {t('cancel')}
          </button>
          <button type="button" className="lib-btn lib-btn--ghost" onClick={onLater}>
            {t('schedule.later')}
          </button>
          <button type="button" className="lib-btn lib-btn--primary" disabled={!valid} onClick={() => onSchedule(at)}>
            {t('schedule.save')}
          </button>
        </>
      }
    >
      <div className="ofh-when">
        <label className="ofp-field">
          <span className="ofp-field-label">{t('schedule.date')}</span>
          <input type="date" className="ofp-input" min={dateValue(new Date())} value={date} onChange={(e) => setDate(e.target.value)} />
        </label>
        <label className="ofp-field">
          <span className="ofp-field-label">{t('schedule.time')}</span>
          <input type="time" className="ofp-input" value={time} onChange={(e) => setTime(e.target.value)} />
        </label>
      </div>
      {!valid && <p className="ofp-modal-note ofh-note-bad">{t('err.pastDate')}</p>}
      <p className="ofp-modal-note">{laterNote ?? t('schedule.laterNote')}</p>
    </Dialog>
  )
}

// ── Add to list (another creator's post) ────────────────────────────────────

export interface ListOption {
  id: string
  name: string
  users: number
}

export function ListPicker({ lists, loading, onPick, onClose }: { lists: ListOption[]; loading: boolean; onPick: (l: ListOption) => void; onClose: () => void }) {
  const { t } = useTranslation('ofHome')
  return (
    <Dialog title={t('feed.addToList')} onClose={onClose}>
      <div className="ofh-pick-list" role="menu" aria-label={t('feed.addToList')}>
        {lists.map((l) => (
          <button key={l.id} type="button" role="menuitem" className="ofh-pick ofh-pick--btn" onClick={() => onPick(l)}>
            <FeatherIcon name="list" size={16} />
            <span className="ofh-pick-text">
              <span className="ofh-pick-name">{l.name}</span>
              <span className="ofh-pick-sub">{t('feed.listUsers', { count: l.users })}</span>
            </span>
          </button>
        ))}
      </div>
      <ListLoading loading={loading} empty={!lists.length} />
      {!loading && lists.length === 0 && <p className="lib-empty">{t('feed.noLists')}</p>}
    </Dialog>
  )
}
